'use client';

import type { ComponentProps } from 'react';

import { Dialog as DialogPrimitive } from '@base-ui-components/react/dialog';
import { clsxm } from '@vs-shop/utils';
import { XIcon } from 'lucide-react';

export const Dialog = (props: ComponentProps<typeof DialogPrimitive.Root>) => (
  <DialogPrimitive.Root data-slot="dialog" {...props} />
);

export const DialogTrigger = (props: ComponentProps<typeof DialogPrimitive.Trigger>) => (
  <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
);

export const DialogPortal = (props: ComponentProps<typeof DialogPrimitive.Portal>) => (
  <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
);

export const DialogClose = (props: ComponentProps<typeof DialogPrimitive.Close>) => (
  <DialogPrimitive.Close data-slot="dialog-close" {...props} />
);

// Base UI calls the dim layer Backdrop (Radix's Overlay). Kept under the
// shadcn-familiar `DialogOverlay` name for the public API.
export const DialogOverlay = ({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Backdrop>) => (
  <DialogPrimitive.Backdrop
    data-slot="dialog-overlay"
    className={clsxm(
      'bg-background-inverted/40 fixed inset-0 z-(--z-max)',
      'transition-opacity duration-200',
      'data-ending-style:opacity-0 data-starting-style:opacity-0',
      className
    )}
    {...props}
  />
);

/**
 * The dialog's own box, unpainted.
 *
 * {@link DialogContent} is the centred card most dialogs want — a portal, a
 * backdrop, a max width, padding and a close button, all decided here. A
 * surface that is none of those (a full-bleed lightbox, a media viewer) would
 * have to override every one of those decisions to get back to an empty box, so
 * it composes this instead, alongside {@link DialogPortal} and
 * {@link DialogOverlay}, and paints itself.
 */
export const DialogPopup = (props: ComponentProps<typeof DialogPrimitive.Popup>) => (
  <DialogPrimitive.Popup data-slot="dialog-popup" {...props} />
);

export type DialogContentProps = ComponentProps<typeof DialogPrimitive.Popup> & {
  showCloseButton?: boolean;
  closeButtonLabel?: string;
};

export const DialogContent = ({
  className,
  children,
  showCloseButton = true,
  closeButtonLabel = 'Close',
  ...props
}: DialogContentProps) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Popup
      data-slot="dialog-content"
      className={clsxm(
        'fixed top-[50%] left-[50%] z-(--z-max) grid',
        'bg-background-intense tab:p-6 gap-4 rounded-lg p-4 shadow-lg duration-200 outline-none',
        'tab:max-w-[min(var(--container-lg),calc(100%-2rem))] w-full max-w-[calc(100%-2rem)]',
        'translate-x-[-50%] translate-y-[-50%]',
        'transition-[transform,scale,opacity]',
        'data-starting-style:scale-95 data-starting-style:opacity-0',
        'data-ending-style:scale-95 data-ending-style:opacity-0',
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton ? (
        <DialogPrimitive.Close
          data-slot="dialog-close"
          className={clsxm(
            'absolute top-4 right-4 rounded-xs opacity-70 hover:opacity-100',
            'focus:outline-hidden',
            "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
            'disabled:pointer-events-none'
          )}
        >
          <XIcon />
          <span className="sr-only">{closeButtonLabel}</span>
        </DialogPrimitive.Close>
      ) : null}
    </DialogPrimitive.Popup>
  </DialogPortal>
);

export const DialogHeader = ({ className, ...props }: ComponentProps<'div'>) => (
  <div data-slot="dialog-header" className={clsxm('flex flex-col gap-2', className)} {...props} />
);

export const DialogFooter = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="dialog-footer"
    className={clsxm('tab:flex-row tab:justify-end flex flex-col-reverse gap-2', className)}
    {...props}
  />
);

export const DialogTitle = ({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title
    data-slot="dialog-title"
    className={clsxm('text-xl leading-none font-semibold', className)}
    {...props}
  />
);

export const DialogDescription = ({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    data-slot="dialog-description"
    className={clsxm('text-sm', className)}
    {...props}
  />
);
